import { useCallback, useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { SchoolOffersModal } from "@/components/SchoolOffersModal";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";

export default function AdminSchools() {
  const [schools, setSchools] = useState([]);
  const [editing, setEditing] = useState(null);
  const [offersOf, setOffersOf] = useState(null);
  const closeOffers = useCallback(() => setOffersOf(null), []);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/schools");
      setSchools(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      await api.put(`/admin/schools/${editing.id}/contract`, {
        contract_note: editing.contract_note || "",
        active: editing.active,
      });
      const pct = Number(editing.commission_percent);
      if (!Number.isNaN(pct)) {
        await api.patch(`/admin/schools/${editing.id}/commission`, { rate_percent: pct });
      }
      toast.success("Contrat mis à jour.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  const openEdit = (s) => setEditing({
    ...s,
    commission_percent: Math.round((s.commission_rate != null ? s.commission_rate : 0.15) * 100),
  });

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Auto-écoles partenaires</h1>
        <p className="mt-2 text-neutral-500">
          Pilotage du réseau sur le <strong>nouveau modèle</strong> : offres de permis accéléré publiées, places
          déclarées par mois, élèves inscrits et commission due. Commission par défaut : <strong>15% du forfait</strong>.
          Seul l'administrateur Plan Permis peut modifier le taux appliqué à une auto-école.
        </p>

        <div className="mt-6 overflow-x-auto border border-neutral-200 rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="admin-schools-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-3">Auto-école</th>
                <th className="px-4 py-3">Ville / CP</th>
                <th className="px-4 py-3">Offres</th>
                <th className="px-4 py-3">Places/mois</th>
                <th className="px-4 py-3">Inscrits</th>
                <th className="px-4 py-3">Commission due</th>
                <th className="px-4 py-3">Taux</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {schools.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-neutral-500" data-testid="admin-no-schools">
                    Aucune auto-école inscrite.
                  </td>
                </tr>
              )}
              {schools.map((s) => (
                <tr key={s.id} data-testid={`school-row-${s.id}`} className="hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-semibold">
                    {s.name}
                    <span className="block text-xs font-normal text-neutral-500">{s.email}</span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {s.city} <span className="text-neutral-400">{s.postal_code}</span>
                  </td>
                  <td className="px-4 py-3" data-testid={`school-offers-${s.id}`}>
                    <button type="button" onClick={() => setOffersOf(s)} data-testid={`view-offers-${s.id}`}
                      className="group inline-flex flex-col items-start text-left" title="Voir le détail des forfaits">
                      <span className="font-semibold text-neutral-800">{s.offers_count ?? 0} <span className="text-xs font-normal text-neutral-400">en ligne</span></span>
                      <span className="mt-0.5 inline-flex items-center gap-1 whitespace-nowrap text-xs font-bold text-brand group-hover:underline"><Eye className="h-3.5 w-3.5" /> Voir les forfaits</span>
                    </button>
                  </td>
                  <td className="px-4 py-3" data-testid={`school-places-${s.id}`}>
                    <span className="font-semibold text-neutral-800">{s.places_upcoming ?? 0}</span>
                    <span className="block text-xs text-neutral-400">à venir</span>
                  </td>
                  <td className="px-4 py-3" data-testid={`school-enrolled-${s.id}`}>
                    <span className="font-semibold text-emerald-700">{s.enrolled_count ?? 0}</span>
                    {(s.active_dossiers ?? 0) > 0 && (
                      <span className="block text-xs text-blue-600">{s.active_dossiers} en cours</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`school-due-${s.id}`}>
                    {(s.commission_due ?? 0) > 0 ? (
                      <span className="font-bold text-neutral-900">{Math.round(s.commission_due)} €</span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`school-contract-${s.id}`}>
                    <span className="font-bold text-brand">{Math.round((s.commission_rate != null ? s.commission_rate : 0.15) * 100)}%</span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        s.active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"
                      }`}
                    >
                      {s.active ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => setOffersOf(s)}
                      data-testid={`view-offers-btn-${s.id}`}
                      className="whitespace-nowrap rounded border border-brand/30 bg-brand/5 px-3 py-1.5 text-xs font-bold text-brand transition-colors hover:bg-brand/10"
                    >
                      Forfaits
                    </button>
                    <button
                      onClick={() => openEdit(s)}
                      data-testid={`edit-contract-${s.id}`}
                      className="rounded border border-neutral-300 px-3 py-1.5 text-xs font-bold transition-colors hover:border-brand hover:text-brand"
                    >
                      Modifier le contrat
                    </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editing && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6" data-testid="contract-modal">
              <h2 className="text-xl font-extrabold tracking-tight">Contrat — {editing.name}</h2>
              <div className="mt-4 rounded-lg border border-brand/20 bg-brand/5 p-3">
                <label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Taux de commission (% du forfait)</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    data-testid="contract-commission-input"
                    type="number" min="0" max="100"
                    value={editing.commission_percent}
                    onChange={(e) => setEditing({ ...editing, commission_percent: e.target.value })}
                    className="w-28 rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
                  />
                  <span className="text-sm font-bold text-neutral-600">%</span>
                </div>
                <p className="mt-1 text-xs text-neutral-500">Appliqué à chaque inscription finalisée. Défaut : 15%.</p>
              </div>
              <div className="mt-4 space-y-3">
                <textarea
                  data-testid="contract-note-input"
                  value={editing.contract_note || ""}
                  onChange={(e) => setEditing({ ...editing, contract_note: e.target.value })}
                  rows={3}
                  placeholder="Notes du contrat (durée, conditions…)"
                  className="w-full rounded-lg border border-neutral-300 px-4 py-3 outline-none focus:border-brand transition-colors"
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    data-testid="contract-active-checkbox"
                    type="checkbox"
                    checked={editing.active}
                    onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                    className="h-4 w-4 accent-brand"
                  />
                  Partenaire actif (offres visibles des élèves, reçoit des dossiers)
                </label>
              </div>
              <div className="mt-6 flex gap-3">
                <button
                  onClick={save}
                  data-testid="contract-save-btn"
                  className="flex-1 rounded-md bg-brand px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-hover"
                >
                  Enregistrer
                </button>
                <button
                  onClick={() => setEditing(null)}
                  data-testid="contract-cancel-btn"
                  className="rounded-md border border-neutral-300 px-5 py-3 text-sm font-bold transition-colors hover:border-neutral-900"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <SchoolOffersModal school={offersOf} onClose={closeOffers} />
    </Shell>
  );
}
